import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Coins, Droplets, ShieldCheck, UserMinus } from 'lucide-react';
import { adminAPI, profitSharingAPI, stewardAPI, unitAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import {
  calculateComplianceRate,
  extractPhotoUrls,
  formatCurrency,
  formatDateTime,
  formatNumber,
  getInspectionDisplayOverallStatus,
  getDaysElapsedInMonth,
  getUserDisplayName,
  getUserPhone,
  isSameMonth
} from '../utils/operations';

const getReviewBadgeClass = (status) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-400/10 text-emerald-300';
    case 'rejected':
      return 'bg-rose-400/10 text-rose-300';
    case 'pending_review':
      return 'bg-amber-400/10 text-amber-300';
    default:
      return 'bg-slate-700 text-slate-300';
  }
};

const getReviewBadgeLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'Reviewed';
    case 'rejected':
      return 'Returned';
    case 'pending_review':
      return 'Pending review';
    default:
      return 'Not reviewed';
  }
};

const getOverallBadgeClass = () => 'bg-emerald-400/10 text-emerald-300';

const getOverallBadgeLabel = (status) => status ? 'Inspection complete' : 'Not uploaded';

const tabs = [
  { key: 'units', label: 'Managed sites' },
  { key: 'checkins', label: 'Inspection history' },
  { key: 'revenue', label: 'Revenue details' }
];

function SummaryCard({ label, value, hint = '', valueClassName = '' }) {
  return (
    <div className="admin-card rounded-2xl p-5">
      <div className="admin-text-muted text-[11px] font-bold uppercase tracking-[0.2em]">{label}</div>
      <div className={`admin-text-primary mt-3 text-3xl font-black ${valueClassName}`}>{value}</div>
      {hint ? <div className="admin-text-secondary mt-1 text-xs">{hint}</div> : null}
    </div>
  );
}

export default function StewardDetail() {
  const currentAdmin = useAuthStore((state) => state.user);
  const canManagePartnerBindings = hasAdminPermission(currentAdmin, 'manage_partner_bindings');
  const canViewPartnerNetwork = hasAdminPermission(currentAdmin, 'view_partner_network');
  const canReviewCheckins = hasAdminPermission(currentAdmin, 'review_steward_checkins');
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('units');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    loadData();
    // The detail page only needs to refetch when the route id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, unitsRes, treeRes, historyRes, complianceRes] = await Promise.all([
        adminAPI.getUsersAllPages({ role: 'Steward' }),
        unitAPI.getUnits(),
        adminAPI.getPartnerTree(),
        canReviewCheckins ? stewardAPI.getCheckInHistory({ limit: 500 }) : Promise.resolve({ data: { data: [] } }),
        canViewPartnerNetwork ? stewardAPI.getComplianceSummary() : Promise.resolve({ data: { data: { stewards: [] } } })
      ]);
      const devicesRes = canViewPartnerNetwork
        ? await profitSharingAPI.getUserDevices({ userId: id, role: 'Steward' })
        : null;

      const steward = (usersRes.data?.data || []).find((entry) => String(entry.id) === String(id));
      if (!steward) {
        throw new Error('Steward not found');
      }

      const managedUnits = (unitsRes.data?.data || []).filter((unit) => String(unit.stewardId) === String(id));
      const partnerTree = treeRes.data?.data || [];
      const rp = partnerTree.find((entry) => (entry.children || []).some((child) => String(child.id) === String(id))) || null;
      const revenue = devicesRes?.data?.data || { devices: [], totalProfit: 0 };
      const allCheckins = historyRes.data?.data || [];
      const checkins = allCheckins.filter((record) => String(record.stewardId) === String(id));
      const monthlyCheckins = checkins.filter((record) => isSameMonth(record.createdAt));
      const compliance = (complianceRes.data?.data?.stewards || []).find((entry) => String(entry.stewardId) === String(id)) || {};

      const salesByUnitId = {};
      (revenue.devices || []).forEach((entry) => {
        salesByUnitId[String(entry.unitId)] = entry;
      });

      setData({
        steward,
        rp,
        managedUnits,
        checkins,
        monthlyCheckins,
        revenue,
        salesByUnitId,
        pendingReviewCount: monthlyCheckins.filter((record) => record.reviewStatus === 'pending_review').length,
        rejectedReviewCount: checkins.filter((record) => record.reviewStatus === 'rejected').length,
        complianceRate: calculateComplianceRate({
          completedCheckins: Number(compliance.completedCheckins ?? monthlyCheckins.length),
          unitCount: managedUnits.length,
          daysElapsed: getDaysElapsedInMonth()
        })
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load steward detail');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStewardRole = async () => {
    if (!canManagePartnerBindings) return;
    if (!data?.steward) return;
    const name = getUserDisplayName(data.steward);
    if (!window.confirm(`Remove ${name} from the steward role? This will unassign their units but will not delete the account, inspection history, or historical income.`)) {
      return;
    }

    setRemoving(true);
    try {
      const res = await adminAPI.removeUserRole(data.steward.id, 'Steward');
      const unboundUnits = res.data?.data?.unboundUnits ?? 0;
      alert(`Steward role removed and ${unboundUnits} units unassigned.`);
      navigate('/stewards');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to remove steward role');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-slate-300">Loading steward details...</div>;
  }

  if (error || !data) {
    return (
      <div className="space-y-4 p-8 text-white">
        <button onClick={() => navigate('/stewards')} className="inline-flex items-center gap-2 text-sm text-cyan-300">
          <ArrowLeft size={16} /> Back to steward list
        </button>
        <div className="rounded-3xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-200">{error || 'Steward not found'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-8 text-white">
      <header className="space-y-6 rounded-[2rem] border border-white/5 bg-slate-800/50 p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <button onClick={() => navigate('/stewards')} className="mb-4 inline-flex items-center gap-2 text-sm text-cyan-300">
              <ArrowLeft size={16} /> Back to steward list
            </button>
            <h2 className="text-3xl font-black">{getUserDisplayName(data.steward)}</h2>
            <p className="mt-2 text-sm text-slate-400">
              {getUserPhone(data.steward)} | RP: {data.rp?.name || 'Unassigned'} | Managed units: {formatNumber(data.managedUnits.length)}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {canManagePartnerBindings && <button
              type="button"
              onClick={handleRemoveStewardRole}
              disabled={removing}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-black text-rose-700 transition hover:border-rose-300 hover:bg-rose-100 disabled:opacity-60"
            >
              <UserMinus size={16} />
              {removing ? 'Removing...' : 'Remove steward role'}
            </button>}
            {canReviewCheckins && <Link
              to="/steward-checkins"
              className="inline-flex items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm font-bold text-cyan-200 transition hover:bg-cyan-400 hover:text-slate-950"
            >
              View all inspection records
            </Link>}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {canViewPartnerNetwork && <SummaryCard label="Inspection compliance" value={`${data.complianceRate}%`} valueClassName="text-emerald-300" />}
          {canViewPartnerNetwork && <SummaryCard label="Monthly revenue" value={formatCurrency(data.revenue.totalRevenue || 0)} />}
          {canViewPartnerNetwork && <SummaryCard label="Monthly income distribution" value={formatCurrency(data.revenue.totalProfit || 0)} valueClassName="text-amber-300" />}
        </div>

      </header>

      {canReviewCheckins && <section className="grid gap-4 md:grid-cols-2">
        <SummaryCard label="Pending review inspections" value={formatNumber(data.pendingReviewCount)} hint="Requires review in the admin portal" valueClassName="text-amber-300" />
        <SummaryCard label="Returned inspections" value={formatNumber(data.rejectedReviewCount)} hint="Includes returned inspections from all history" valueClassName="text-rose-300" />
      </section>}

      <div className="flex flex-wrap gap-2 border-b border-white/5 pb-4">
        {(canViewPartnerNetwork ? tabs : tabs.filter((tab) => tab.key !== 'revenue')).filter((tab) => canReviewCheckins || tab.key !== 'checkins').map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab.key ? 'bg-cyan-400 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'units' && (
        <section className="grid gap-5 lg:grid-cols-2">
          {data.managedUnits.map((unit) => {
            const sales = data.salesByUnitId[String(unit.id)] || {};
            return (
              <article key={unit.id} className="rounded-[2rem] border border-white/5 bg-slate-800/50 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold">{unit.deviceId}</h3>
                    <p className="mt-1 text-sm text-slate-400">{unit.location || 'Location not set'}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${unit.status === 'Online' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>
                    {unit.status}
                  </span>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {canViewPartnerNetwork && <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <Droplets size={14} /> Monthly revenue
                    </div>
                    <div className="mt-2 text-xl font-black">{formatCurrency(sales.totalRevenue || 0)}</div>
                    <div className="mt-1 text-xs text-slate-400">Water sold: {formatNumber(sales.totalVolume || 0)} L</div>
                  </div>}

                  {canViewPartnerNetwork && <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <Coins size={14} /> Steward income distribution
                    </div>
                    <div className="mt-2 text-xl font-black text-amber-300">{formatCurrency(sales.stewardProfit || 0)}</div>
                    <div className="mt-1 text-xs text-slate-400">Filter-life data requires a device-side update.</div>
                  </div>}

                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <ShieldCheck size={14} /> Water quality snapshot
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">
                      TDS {unit.tdsValue ?? '-'} | Temperature {unit.temperature ?? '-'}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">pH trends require a dedicated endpoint.</div>
                  </div>

                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <ClipboardCheck size={14} /> Latest maintenance
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white">{formatDateTime(unit.lastMaintenanceAt)}</div>
                    <div className="mt-1 text-xs text-slate-400">Last heartbeat: {formatDateTime(unit.lastHeartbeatAt)}</div>
                  </div>
                </div>
              </article>
            );
          })}

          {data.managedUnits.length === 0 && (
            <div className="rounded-3xl border border-white/5 bg-slate-800/50 p-12 text-center text-slate-400">No units assigned.</div>
          )}
        </section>
      )}

      {canReviewCheckins && activeTab === 'checkins' && (
        <section className="space-y-4">
          {data.checkins.map((record) => {
            const photoUrls = extractPhotoUrls(record);
            const overallStatus = getInspectionDisplayOverallStatus(record);

            return (
              <article key={record.id} className="rounded-[2rem] border border-white/5 bg-slate-800/50 p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="text-lg font-bold text-white">{record.deviceId || `Unit #${record.unitId}`}</div>
                    <div className="mt-1 text-sm text-slate-400">{formatDateTime(record.createdAt)}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${getOverallBadgeClass(overallStatus)}`}>
                      {getOverallBadgeLabel(overallStatus)}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${getReviewBadgeClass(record.reviewStatus)}`}>
                      {getReviewBadgeLabel(record.reviewStatus)}
                    </span>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    Pure-water TDS: <span className="font-semibold text-white">{record.tdsValue ?? '-'}</span>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    pH: <span className="font-semibold text-white">{record.phValue ?? '-'}</span>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    Cleaning: <span className="font-semibold text-white">{record.cleaned ? 'Completed' : 'Not completed'}</span>
                  </div>
                  <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 text-sm text-slate-300">
                    Filter/leak: <span className="font-semibold text-white">{record.filterChecked ? 'Checked' : 'Not checked'} / {record.leakageChecked ? 'Checked' : 'Not checked'}</span>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <div className="space-y-3 rounded-2xl border border-white/5 bg-slate-900/30 p-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Inspection summary</div>
                      <div className="mt-2 text-sm text-slate-200">{record.summary || 'No inspection summary provided'}</div>
                    </div>
                  </div>

                  <div className="space-y-3 rounded-2xl border border-white/5 bg-slate-900/30 p-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Review notes</div>
                      <div className="mt-2 text-sm text-slate-200">{record.reviewNote || 'No review note'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Rejection reason</div>
                      <div className="mt-2 text-sm text-rose-200">{record.rejectionReason || 'No rejection reason'}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Photos</div>
                      <div className="mt-2 flex flex-wrap gap-3">
                        {photoUrls.length > 0 ? photoUrls.map((url) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-cyan-300 underline">
                            View photo
                          </a>
                        )) : (
                          <span className="text-sm text-slate-400">No photos</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}

          {data.checkins.length === 0 && (
            <div className="rounded-3xl border border-white/5 bg-slate-800/50 p-12 text-center text-slate-400">No inspection records.</div>
          )}
        </section>
      )}

      {canViewPartnerNetwork && activeTab === 'revenue' && (
        <section className="rounded-[2rem] border border-white/5 bg-slate-800/50 p-6">
          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard label="Monthly revenue" value={formatCurrency(data.revenue.totalRevenue || 0)} />
            <SummaryCard label="Monthly income distribution" value={formatCurrency(data.revenue.totalProfit || 0)} valueClassName="text-amber-300" />
            <SummaryCard label="Water sold" value={`${formatNumber(data.revenue.totalVolume || 0)} L`} />
          </div>

          <div className="mt-6 overflow-x-auto rounded-2xl border border-white/5">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900/60 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                <tr>
                  <th className="p-4">Unit</th>
                  <th className="p-4">Revenue</th>
                  <th className="p-4">Water sold</th>
                  <th className="p-4">Steward income distribution</th>
                </tr>
              </thead>
              <tbody>
                {(data.revenue.devices || []).map((entry) => (
                  <tr key={`${entry.id}-${entry.unitId}`} className="border-t border-white/5">
                    <td className="p-4">
                      <div className="font-semibold text-white">{entry.unit?.deviceId || entry.deviceId || entry.unitId}</div>
                      <div className="text-xs text-slate-400">{entry.unit?.location || entry.unit?.deviceName || 'Location not set'}</div>
                    </td>
                    <td className="p-4">{formatCurrency(entry.totalRevenue || 0)}</td>
                    <td className="p-4">{formatNumber(entry.totalVolume || 0)} L</td>
                    <td className="p-4 text-amber-300">{formatCurrency(entry.stewardProfit || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

    </div>
  );
}
